import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, PencilSimple, MagnifyingGlass, UploadSimple, Trash, Door } from "@phosphor-icons/react";
import HostelRoomsDialog from "@/components/HostelRoomsDialog";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const logoUrl = (path) => path ? `${API_BASE}/api/files/logo?path=${encodeURIComponent(path)}` : null;

function LogoUploader({ orgType, orgId, currentPath, onChange }) {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = useState(false);
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/admin/${orgType}/${orgId}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo updated");
      onChange?.(res.data?.logo_path);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  const clear = async () => {
    if (!window.confirm("Remove this logo?")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/${orgType}/${orgId}/logo`);
      onChange?.(null);
      toast.success("Logo cleared");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2">
      {currentPath ? (
        <img src={logoUrl(currentPath)} alt="logo" className="w-8 h-8 rounded-md object-cover border border-[var(--vs-border)]" data-testid={`${orgType}-logo-img-${orgId}`} />
      ) : (
        <div className="w-8 h-8 rounded-md border border-dashed border-[var(--vs-border)] grid place-items-center text-[10px] text-[var(--vs-text-secondary)]" data-testid={`${orgType}-logo-empty-${orgId}`}>—</div>
      )}
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={onPick} data-testid={`${orgType}-logo-input-${orgId}`} />
      <Button size="sm" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()} data-testid={`${orgType}-logo-upload-${orgId}`}>
        <UploadSimple size={12} className="mr-1" />
        {currentPath ? "Replace" : "Upload"}
      </Button>
      {currentPath && (
        <Button size="sm" variant="ghost" disabled={busy} onClick={clear} data-testid={`${orgType}-logo-clear-${orgId}`}>
          <Trash size={12} />
        </Button>
      )}
    </div>
  );
}

const OCC_ALERT_CLASS = { red: "occ-red", amber: "occ-amber", green: "occ-green" };
const STATUS_CLASS = {
  active: "border-[var(--vs-success)]/40 text-[var(--vs-success)] bg-[var(--vs-success)]/10",
  disabled: "border-[var(--vs-warning)]/40 text-[var(--vs-warning)] bg-[var(--vs-warning)]/10",
};
const STATUS_CLASS_DEFAULT = "border-[var(--vs-danger)]/40 text-[var(--vs-danger)] bg-[var(--vs-danger)]/10";

function classesAssignedLabel(u) {
  if (u.classes_assigned && u.classes_assigned.length > 0) {
    return ` (Classes ${u.classes_assigned.join(", ")})`;
  }
  if (u.class_assigned) return ` (Class ${u.class_assigned})`;
  return "";
}

function OccupancyAlert({ alert, pct }) {
  const cls = OCC_ALERT_CLASS[alert] || OCC_ALERT_CLASS.green;
  return <span className={`inline-flex items-center gap-1.5 text-xs font-mono px-2 py-1 rounded-md ${cls}`}>{pct}% • {alert?.toUpperCase()}</span>;
}

function StatusBadge({ status }) {
  const cls = STATUS_CLASS[status] || STATUS_CLASS_DEFAULT;
  return <Badge variant="outline" className={cls}>{status || "active"}</Badge>;
}

export function HostelsPage() {
  const [hostels, setHostels] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", location: "", total_beds: 100 });
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [roomsTarget, setRoomsTarget] = useState(null);

  const refresh = () => api.get("/admin/hostels").then((r) => setHostels(r.data));
  useEffect(() => { refresh(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/hostels", { ...form, total_beds: Number(form.total_beds) });
      toast.success("Hostel created");
      setOpen(false);
      setForm({ name: "", location: "", total_beds: 100 });
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  const startEdit = (h) => {
    setEditTarget(h);
    setEditForm({ name: h.name, location: h.location, total_beds: h.total_beds, status: h.status || "active" });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editForm, total_beds: Number(editForm.total_beds) };
      await api.patch(`/admin/hostels/${editTarget.id}`, payload);
      toast.success("Hostel updated");
      setEditTarget(null);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  return (
    <DashboardLayout title="Hostels" subtitle="Manage all hostels under the trust.">
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-[var(--vs-text-secondary)]">{hostels.length} hostels • capacity 5,000 max</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-hostel-btn" className="bg-[var(--vs-secondary)] hover:bg-[var(--vs-secondary-hover)] text-[var(--vs-bg)]">
              <Plus size={14} className="mr-1.5" /> New hostel
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create hostel</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Location</Label><Input required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
              <div><Label>Total beds</Label><Input required type="number" value={form.total_beds} onChange={(e) => setForm({ ...form, total_beds: e.target.value })} /></div>
              <Button type="submit" className="bg-[var(--vs-secondary)] hover:bg-[var(--vs-secondary-hover)] text-[var(--vs-bg)] w-full">Create</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="vs-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--vs-bg-subtle)]">
            <tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">
              <th className="px-4 py-3">Name</th><th className="px-4 py-3">Location</th>
              <th className="px-4 py-3">Beds</th><th className="px-4 py-3">Occupancy</th>
              <th className="px-4 py-3">Status</th><th className="px-4 py-3">Logo</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {hostels.map((h) => (
              <tr key={h.id} data-testid={`hostel-row-${h.id}`} className="border-t border-[var(--vs-border)]">
                <td className="px-4 py-3 font-medium">{h.name}</td>
                <td className="px-4 py-3">{h.location}</td>
                <td className="px-4 py-3 font-mono text-xs">{h.occupied_beds}/{h.total_beds}</td>
                <td className="px-4 py-3"><OccupancyAlert alert={h.alert} pct={h.occupancy_pct} /></td>
                <td className="px-4 py-3"><StatusBadge status={h.status} /></td>
                <td className="px-4 py-3">
                  <LogoUploader
                    orgType="hostels"
                    orgId={h.id}
                    currentPath={h.logo_path}
                    onChange={refresh}
                  />
                </td>
                <td className="px-4 py-3">
                  <Button size="sm" variant="outline" data-testid={`hostel-rooms-${h.id}`} onClick={() => setRoomsTarget(h)} className="mr-2">
                    <Door size={12} className="mr-1.5" /> Rooms
                  </Button>
                  <Button size="sm" variant="outline" data-testid={`edit-hostel-${h.id}`} onClick={() => startEdit(h)}>
                    <PencilSimple size={12} className="mr-1.5" /> Edit
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editTarget} onOpenChange={(v) => !v && setEditTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit hostel</DialogTitle></DialogHeader>
          {editTarget && (
            <form onSubmit={saveEdit} className="space-y-3">
              <div><Label>Name</Label><Input data-testid="edit-hostel-name" value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
              <div><Label>Location</Label><Input data-testid="edit-hostel-location" value={editForm.location || ""} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} /></div>
              <div><Label>Total beds (capacity)</Label><Input data-testid="edit-hostel-beds" type="number" value={editForm.total_beds || 0} onChange={(e) => setEditForm({ ...editForm, total_beds: e.target.value })} /></div>
              <div>
                <Label>Status</Label>
                <Select value={editForm.status || "active"} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger data-testid="edit-hostel-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="disabled">Disabled (no new admissions)</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="save-hostel" type="submit" className="bg-[var(--vs-secondary)] hover:bg-[var(--vs-secondary-hover)] text-[var(--vs-bg)] w-full">Save changes</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <HostelRoomsDialog
        hostel={roomsTarget}
        open={!!roomsTarget}
        onOpenChange={(v) => !v && setRoomsTarget(null)}
      />
    </DashboardLayout>
  );
}

export function SchoolsPage() {
  const [schools, setSchools] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", location: "", total_capacity: 500 });
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({});

  const refresh = () => api.get("/admin/schools").then((r) => setSchools(r.data));
  useEffect(() => { refresh(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/schools", { ...form, total_capacity: Number(form.total_capacity) });
      toast.success("School created");
      setOpen(false);
      setForm({ name: "", location: "", total_capacity: 500 });
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  const startEdit = (s) => {
    setEditTarget(s);
    setEditForm({ name: s.name, location: s.location, total_capacity: s.total_capacity, status: s.status || "active" });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/admin/schools/${editTarget.id}`, { ...editForm, total_capacity: Number(editForm.total_capacity) });
      toast.success("School updated");
      setEditTarget(null);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  return (
    <DashboardLayout title="Schools" subtitle="Manage all schools under the trust.">
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-[var(--vs-text-secondary)]">{schools.length} schools • capacity 5,000 max</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-school-btn" className="bg-[var(--vs-secondary)] hover:bg-[var(--vs-secondary-hover)] text-[var(--vs-bg)]">
              <Plus size={14} className="mr-1.5" /> New school
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create school</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Location</Label><Input required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
              <div><Label>Total capacity</Label><Input required type="number" value={form.total_capacity} onChange={(e) => setForm({ ...form, total_capacity: e.target.value })} /></div>
              <Button type="submit" className="bg-[var(--vs-secondary)] hover:bg-[var(--vs-secondary-hover)] text-[var(--vs-bg)] w-full">Create</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="vs-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--vs-bg-subtle)]"><tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">
            <th className="px-4 py-3">Name</th><th className="px-4 py-3">Location</th>
            <th className="px-4 py-3">Capacity</th><th className="px-4 py-3">Enrolled</th>
            <th className="px-4 py-3">Status</th><th className="px-4 py-3">Logo</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {schools.map((s) => (
              <tr key={s.id} data-testid={`school-row-${s.id}`} className="border-t border-[var(--vs-border)]">
                <td className="px-4 py-3 font-medium">{s.name}</td>
                <td className="px-4 py-3">{s.location}</td>
                <td className="px-4 py-3 font-mono text-xs">{s.total_capacity}</td>
                <td className="px-4 py-3 font-mono text-xs">{s.enrolled}</td>
                <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                <td className="px-4 py-3">
                  <LogoUploader orgType="schools" orgId={s.id} currentPath={s.logo_path} onChange={refresh} />
                </td>
                <td className="px-4 py-3">
                  <Button size="sm" variant="outline" data-testid={`edit-school-${s.id}`} onClick={() => startEdit(s)}>
                    <PencilSimple size={12} className="mr-1.5" /> Edit
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editTarget} onOpenChange={(v) => !v && setEditTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit school</DialogTitle></DialogHeader>
          {editTarget && (
            <form onSubmit={saveEdit} className="space-y-3">
              <div><Label>Name</Label><Input data-testid="edit-school-name" value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
              <div><Label>Location</Label><Input data-testid="edit-school-location" value={editForm.location || ""} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} /></div>
              <div><Label>Total capacity</Label><Input data-testid="edit-school-capacity" type="number" value={editForm.total_capacity || 0} onChange={(e) => setEditForm({ ...editForm, total_capacity: e.target.value })} /></div>
              <div>
                <Label>Status</Label>
                <Select value={editForm.status || "active"} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger data-testid="edit-school-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="disabled">Disabled (no new admissions)</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="save-school" type="submit" className="bg-[var(--vs-secondary)] hover:bg-[var(--vs-secondary-hover)] text-[var(--vs-bg)] w-full">Save changes</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export function UsersPage() {
  const [users, setUsers] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [schools, setSchools] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    email: "", name: "", password: "", role: "donor", hostel_id: "", school_id: "",
    subject: "", class_assigned: "", classes_assigned_csv: "",
  });

  const refresh = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => {
    refresh();
    api.get("/admin/hostels").then((r) => setHostels(r.data));
    api.get("/admin/schools").then((r) => setSchools(r.data));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.hostel_id) delete payload.hostel_id;
      if (!payload.school_id) delete payload.school_id;
      if (!payload.subject) delete payload.subject;
      // For teachers: parse comma-separated classes
      if (payload.role === "teacher" && payload.classes_assigned_csv) {
        const list = payload.classes_assigned_csv.split(",").map((c) => c.trim()).filter(Boolean);
        payload.classes_assigned = list;
        // backward-compat: also fill class_assigned with first class
        payload.class_assigned = list[0] || "";
      }
      delete payload.classes_assigned_csv;
      if (!payload.class_assigned) delete payload.class_assigned;
      if (!payload.classes_assigned || !payload.classes_assigned.length) delete payload.classes_assigned;
      await api.post("/admin/users", payload);
      toast.success("User created");
      setOpen(false);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  return (
    <DashboardLayout title="Users" subtitle="All accounts across all roles. Bcrypt + AES-256.">
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-[var(--vs-text-secondary)]">{users.length} users</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-user-btn" className="bg-[var(--vs-secondary)] hover:bg-[var(--vs-secondary-hover)] text-[var(--vs-bg)]">
              <Plus size={14} className="mr-1.5" /> New user
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create user</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label>Email</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              </div>
              <div><Label>Password</Label><Input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                    <SelectItem value="hostel_admin">Hostel Admin</SelectItem>
                    <SelectItem value="school_admin">School Admin</SelectItem>
                    <SelectItem value="teacher">Teacher</SelectItem>
                    <SelectItem value="donor">Donor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.role === "hostel_admin" && (
                <div>
                  <Label>Hostel</Label>
                  <Select value={form.hostel_id} onValueChange={(v) => setForm({ ...form, hostel_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{hostels.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {(form.role === "school_admin" || form.role === "teacher") && (
                <div>
                  <Label>School</Label>
                  <Select value={form.school_id} onValueChange={(v) => setForm({ ...form, school_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{schools.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {form.role === "teacher" && (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Math" /></div>
                  <div><Label>Class</Label><Input value={form.class_assigned} onChange={(e) => setForm({ ...form, class_assigned: e.target.value })} placeholder="8" /></div>
                </div>
              )}
              <Button type="submit" className="bg-[var(--vs-secondary)] hover:bg-[var(--vs-secondary-hover)] text-[var(--vs-bg)] w-full">Create</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="vs-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--vs-bg-subtle)]"><tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">
            <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th>
            <th className="px-4 py-3">Role</th><th className="px-4 py-3">Scope</th>
          </tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-t border-[var(--vs-border)]">
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                <td className="px-4 py-3"><Badge variant="outline" className="border-[var(--vs-secondary)]/40 text-[var(--vs-secondary)] capitalize">{u.role.replace("_", " ")}</Badge></td>
                <td className="px-4 py-3 text-xs text-[var(--vs-text-secondary)]">
                  {u.hostel_id ? `Hostel #${u.hostel_id.slice(0, 8)}` : ""}
                  {u.school_id ? `School #${u.school_id.slice(0, 8)}` : ""}
                  {u.subject ? ` • ${u.subject}` : ""}
                  {classesAssignedLabel(u)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}

export function AuditPage() {
  const [logs, setLogs] = useState([]);
  const [filters, setFilters] = useState({ q: "", actor_role: "all", action: "", entity: "" });

  const fetchLogs = () => {
    const params = new URLSearchParams();
    params.set("limit", "300");
    if (filters.q) params.set("q", filters.q);
    if (filters.actor_role && filters.actor_role !== "all") params.set("actor_role", filters.actor_role);
    if (filters.action) params.set("action", filters.action);
    if (filters.entity) params.set("entity", filters.entity);
    api.get(`/admin/audit-logs?${params.toString()}`).then((r) => setLogs(r.data));
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    fetchLogs();
  };

  const reset = () => {
    setFilters({ q: "", actor_role: "all", action: "", entity: "" });
    setTimeout(fetchLogs, 0);
  };

  return (
    <DashboardLayout title="Audit Trail" subtitle="Immutable record of every privileged action.">
      <form onSubmit={onSearch} className="vs-card p-4 mb-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
        <div className="md:col-span-2">
          <Label className="text-[11px] uppercase tracking-widest font-mono text-[var(--vs-secondary)]">Search</Label>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--vs-text-secondary)]" />
            <Input
              data-testid="audit-search-q"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              placeholder="Email, action or entity"
              className="pl-9 bg-white"
            />
          </div>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest font-mono text-[var(--vs-secondary)]">Role</Label>
          <Select value={filters.actor_role} onValueChange={(v) => setFilters({ ...filters, actor_role: v })}>
            <SelectTrigger data-testid="audit-role-select" className="bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value="super_admin">Super Admin</SelectItem>
              <SelectItem value="hostel_admin">Hostel Admin</SelectItem>
              <SelectItem value="school_admin">School Admin</SelectItem>
              <SelectItem value="teacher">Teacher</SelectItem>
              <SelectItem value="donor">Donor</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest font-mono text-[var(--vs-secondary)]">Action</Label>
          <Input data-testid="audit-action-input" value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} placeholder="login / issue_certificate" className="bg-white" />
        </div>
        <div className="flex gap-2">
          <Button data-testid="audit-search-btn" type="submit" className="bg-[var(--vs-secondary)] hover:bg-[var(--vs-secondary-hover)] text-[var(--vs-bg)]">Search</Button>
          <Button data-testid="audit-reset-btn" type="button" variant="outline" onClick={reset}>Reset</Button>
        </div>
      </form>

      <div className="vs-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--vs-bg-subtle)]">
            <tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">
              <th className="px-4 py-3">Timestamp</th>
              <th className="px-4 py-3">Actor</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Action</th>
              <th className="px-4 py-3">Entity</th>
              <th className="px-4 py-3">IP</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((l) => (
              <tr key={l.id} className="border-t border-[var(--vs-border)]">
                <td className="px-4 py-3 font-mono text-xs">{new Date(l.created_at).toLocaleString()}</td>
                <td className="px-4 py-3 font-mono text-xs">{l.actor_email}</td>
                <td className="px-4 py-3 capitalize">{l.actor_role?.replace("_", " ")}</td>
                <td className="px-4 py-3 font-mono text-xs">{l.action}</td>
                <td className="px-4 py-3 text-xs">{l.entity}{l.entity_id ? ` #${l.entity_id.slice(0, 8)}` : ""}</td>
                <td className="px-4 py-3 font-mono text-xs">{l.ip || "—"}</td>
              </tr>
            ))}
            {logs.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-[var(--vs-text-secondary)]">No audit events match the filter.</td></tr>}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}
